import { useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronRight, ExternalLink, Mail, Pencil, Phone, Plus, Trash2,
  Check, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useAutosave, AutosaveStatus } from "@/lib/useAutosave";
import { opsApi, money, pct } from "../lib/api";
import ProjectDashboard from "../components/ProjectDashboard";
import EmailRoutingDialog from "../components/EmailRoutingDialog";
import Requirements from "../components/Requirements";
import NoteLog from "../components/NoteLog";
import NeedNote from "@/components/NeedNote";
import AccelerationPanel from "../components/AccelerationPanel";
import Hint from "@/components/Hint";

// Everything the handoff used to spread over two cards now lives in Scope and Terms.
const TERMS = [["inclusions", "Contract Inclusions"], ["exclusions", "Contract Exclusions"],
  ["negotiated_terms", "Negotiated Terms"], ["qualifications", "Important Qualifications"],
  ["gc_acknowledgements", "Written GC Acknowledgements"]];
const CONTRACT_FIELDS = [["ops_contract_date", "Contract Date", "date"],
  ["ops_contract_value", "Contract Value", "number"],
  ["ops_retainage_percent", "Retainage %", "number"],
  ["ops_payment_terms_days", "Payment Terms (Net Days)", "number"],
  ["ops_billing_day", "Billing Day Of Month", "number"]];

function Card({ title, right, children, testId, className = "" }) {
  return (
    <section className={`rounded-xl border border-slate-200 bg-white p-5 ${className}`}
             data-testid={testId}>
      <div className="mb-4 flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        {right}
      </div>
      {children}
    </section>
  );
}

const Row = ({ label, children, testId, className = "", hint }) => (
  <div className={`flex items-baseline gap-3 ${className}`} data-testid={testId}>
    <span className="w-24 shrink-0 text-xs font-medium text-slate-500"><Hint text={hint}>{label}</Hint></span>
    <span className="min-w-0 flex-1 text-sm text-slate-900">{children}</span>
  </div>
);

function Editable({ label, value, onChange, editing, testId, type = "text", readOnly, required, className = "", hint }) {
  if (!editing || readOnly) {
    return (
      <Row label={label} testId={testId ? `${testId}-view` : undefined} className={className} hint={hint}>
        <span className={value ? "" : required ? "font-semibold text-amber-600" : "text-slate-400"}
              data-testid={required && !value ? `${testId}-required` : undefined}>{value || "—"}</span>
      </Row>
    );
  }
  return (
    <Row label={label} className={className} hint={hint}>
      <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
             className="h-8 text-sm" data-testid={testId} />
    </Row>
  );
}

function Collapsible({ title, count, children, testId }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="rounded-lg border border-slate-200" data-testid={testId}>
      <button onClick={() => setOpen((v) => !v)} data-testid={`${testId}-toggle`}
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-semibold text-slate-700">
        {open ? <ChevronDown className="h-3.5 w-3.5 text-slate-400" />
          : <ChevronRight className="h-3.5 w-3.5 text-slate-400" />}
        {title}
        <span className="ml-auto text-[11px] font-normal text-slate-400">{count} item(s)</span>
      </button>
      {open && <div className="border-t border-slate-100 px-3 py-3">{children}</div>}
    </div>
  );
}

/** Project Info: General, the Directory, the money, then the dashboard and the terms. */
export default function InfoTab({ project, reload, onGo }) {
  const [form, setForm] = useState({});
  const [handoff, setHandoff] = useState(project.handoff || {});
  const [editing, setEditing] = useState(false);
  const [options, setOptions] = useState(null);
  const [adding, setAdding] = useState(null);
  const [newContact, setNewContact] = useState({ name: "", email: "", phone: "", role: "" });
  const [newPerson, setNewPerson] = useState({ name: "", email: "", phone: "", app_role: "pm" });
  const [addRole, setAddRole] = useState(null);
  const [pickRole, setPickRole] = useState("pm");
  const [newPersonMode, setNewPersonMode] = useState(false);
  const [routing, setRouting] = useState(false);
  const [gcPicks, setGcPicks] = useState(project.identity.ops_gc_contact_ids || []);

  const pending = useRef({});

  useEffect(() => {
    setForm({
      name: project.identity.name,
      address: project.identity.address,
      city: project.identity.city,
      state: project.identity.state,
      zip_code: project.identity.zip_code,
      ops_reconciliation_notes: project.reconciliation_notes,
      contract_start_date: project.schedule.contract_start_date,
      gc_completion_date: project.schedule.gc_completion_date,
      start_date: project.schedule.start_date,
      abs_completion_date: project.schedule.abs_completion_date,
      ops_contract_date: project.contract.contract_date,
      ops_contract_value: project.contract.contract_value,
      ops_retainage_percent: project.contract.retainage_percent,
      ops_payment_terms_days: project.contract.payment_terms_days ?? "",
      ops_billing_day: project.contract.billing_day ?? "",
      ...pending.current,          // never overwrite a keystroke that has not saved yet
    });
    setHandoff(project.handoff || {});
    setGcPicks(project.identity.ops_gc_contact_ids || []);
  }, [project]);

  useEffect(() => {
    opsApi.contactOptions(project.id).then(setOptions)
      .catch(() => setOptions({ contacts: [], users: [] }));
  }, [project.id]);

  const { status, trigger } = useAutosave(async () => {
    const patch = pending.current;
    pending.current = {};
    if (Object.keys(patch).length) {
      await opsApi.patchProject(project.id, patch);
      await reload?.();          // otherwise a tab switch re-mounts on the pre-edit values
    }
  });
  const save = (patch) => { pending.current = { ...pending.current, ...patch }; trigger(pending.current); };
  const set = (key, value) => { setForm((p) => ({ ...p, [key]: value })); save({ [key]: value }); };

  const saveRequirements = async (flags) => {
    try {
      await opsApi.patchRequirements(project.id, flags);
      await reload?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change that requirement");
    }
  };

  const addNote = async (text) => {
    try {
      await opsApi.addNote(project.id, text);
      await reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add that note"); }
  };

  const saveTeam = async (team) => {
    try {
      await opsApi.saveTeam(project.id, team);
      await reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save the team"); }
  };

  const assign = (roleKey, email) => {
    if (!email) return;
    const current = teamRoles.find((r) => r.key === roleKey)?.emails || [];
    if (current.includes(email)) return;
    saveTeam({ [roleKey]: [...current, email] });
  };

  const unassign = (roleKey, email) => {
    const current = teamRoles.find((r) => r.key === roleKey)?.emails || [];
    saveTeam({ [roleKey]: current.filter((e) => e !== email) });
  };

  const createPerson = async (roleKey) => {
    if (!newPerson.email.trim()) { toast.error("An email address is required"); return; }
    try {
      await opsApi.addTeamMember(project.id, { ...newPerson, role_key: roleKey });
      toast.success(`${newPerson.name || newPerson.email} added to this job`);
      setNewPerson({ name: "", email: "", phone: "", app_role: "pm" });
      setNewPersonMode(false);
      setAddRole(null);
      setOptions(await opsApi.contactOptions(project.id));
      await reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add that person"); }
  };


  const createContact = async () => {
    if (!newContact.name.trim()) { toast.error("A name is required"); return; }
    try {
      const res = await opsApi.createContact(project.id, newContact);
      toast.success(`${res.contact.name} added to the directory`);
      setAdding(null);
      setNewContact({ name: "", email: "", phone: "", role: "" });
      setOptions(await opsApi.contactOptions(project.id));
      await reload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add that contact"); }
  };

  const f = project.financials;
  const sch = project.schedule;
  const req = project.requirements || { flags: [], editable: false, source: "project" };
  const teamRoles = project.team?.roles || [];
  const members = project.team?.members || {};
  const contacts = options?.contacts || [];
  const users = options?.users || [];
  const gcPicked = gcPicks;
  const termsCount = TERMS.reduce((n, [k]) => n + (handoff[k] || []).length, 0);
  const routingPeople = [
    ...teamRoles.flatMap((r) => r.emails.map((e) => ({
      email: e, name: members[e]?.name || e, group: "ABS" }))),
    ...contacts.filter((c) => c.email)
      .map((c) => ({ email: c.email, name: c.name, group: "GC" })),
  ].filter((p, i, all) => all.findIndex((x) => x.email === p.email) === i);

  const toggleGcContact = async (id) => {
    // Ticked straight away and saved on the spot — a debounced patch would compute the next
    // list from stale state and drop the previous tick.
    const next = gcPicks.includes(id) ? gcPicks.filter((x) => x !== id) : [...gcPicks, id];
    setGcPicks(next);
    try {
      await opsApi.patchProject(project.id, { ops_gc_contact_ids: next });
      await reload?.();
    } catch (e) {
      setGcPicks(gcPicks);
      toast.error(e?.response?.data?.detail || "Could not save that contact");
    }
  };

  // Read-only: these lists are what Pre-Con pushed at Ready (contract review → reconcile → handoff).
  const listBlock = (key, label) => (
    <div key={key} data-testid={`ops-handoff-${key}`}>
      <p className="text-xs font-semibold text-slate-600">{label}</p>
      <ul className="mt-1 max-h-60 space-y-1 overflow-y-auto pr-1"
          data-testid={`ops-handoff-list-${key}`}>
        {(handoff[key] || []).length === 0 && <li className="text-xs text-slate-400">Nothing recorded</li>}
        {(handoff[key] || []).map((row) => (
          <li key={row.id} className="flex items-start gap-2 text-sm text-slate-700">
            <span className="flex-1">
              {row.text}
              {row.scope && <span className="ml-1 text-xs text-slate-400">({row.scope})</span>}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="ops-info-tab">
      <ProjectDashboard dashboard={project.dashboard} health={f.budget_health} onGo={onGo} />

      <div className="flex justify-end"><AutosaveStatus status={status} /></div>

      <div className="grid gap-5 lg:grid-cols-3">
        {/* ── General ─────────────────────────────────────────────── */}
        <Card title="General" testId="ops-info-general" className="flex flex-col"
              right={<Button variant={editing ? "default" : "outline"} size="sm"
                             onClick={() => setEditing((v) => !v)} data-testid="ops-general-edit">
                {editing ? <><Check className="mr-1.5 h-3.5 w-3.5" /> Done</>
                  : <><Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit</>}
              </Button>}>
          <div className="grid items-start gap-x-6 gap-y-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]" data-testid="ops-info-general-grid">
            <Editable label="Job Name" value={form.name} editing={editing} className="sm:col-span-2"
                      onChange={(v) => set("name", v)} testId="ops-field-name" />
            <Row label="Job Number">
              <span data-testid="ops-info-job-number">
                {project.identity.job_number ?? "not assigned"}
              </span>
            </Row>
            <Editable label="Start" type="date" value={form.contract_start_date} editing={editing} required
                      className="order-10 sm:order-none" onChange={(v) => set("contract_start_date", v)}
                      testId="ops-field-start-date" hint="Contract ABS start date." />
            <Row label="Customer">{project.identity.customer_name || "—"}</Row>
            <Editable label="Completion" type="date" value={form.gc_completion_date} required
                      className="order-10 sm:order-none" editing={editing} onChange={(v) => set("gc_completion_date", v)}
                      testId="ops-field-gc-completion" hint="Contract CO date." />
            {editing ? (
              <div className="flex items-baseline gap-3 sm:row-span-2" data-testid="ops-field-address-edit">
                <span className="w-24 shrink-0 text-xs font-medium text-slate-500">Address</span>
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <Input value={form.address ?? ""} onChange={(e) => set("address", e.target.value)}
                         placeholder="Street" className="h-8 text-sm" data-testid="ops-field-address" />
                  <div className="flex gap-1.5">
                    <Input value={form.city ?? ""} onChange={(e) => set("city", e.target.value)}
                           placeholder="City" className="h-8 flex-1 text-sm" data-testid="ops-field-city" />
                    <Input value={form.state ?? ""} onChange={(e) => set("state", e.target.value)}
                           placeholder="ST" className="h-8 w-14 text-sm uppercase" data-testid="ops-field-state" />
                    <Input value={form.zip_code ?? ""} onChange={(e) => set("zip_code", e.target.value)}
                           placeholder="ZIP" className="h-8 w-24 text-sm" data-testid="ops-field-zip" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-baseline gap-3 sm:row-span-2" data-testid="ops-field-address-view">
                <span className="w-24 shrink-0 text-xs font-medium text-slate-500">Address</span>
                <div className="min-w-0 text-sm text-slate-900">
                  <div className={form.address ? "" : "text-slate-400"} data-testid="ops-field-address-line1">{form.address || "—"}</div>
                  {(form.city || form.state || form.zip_code) && (
                    <div data-testid="ops-field-address-line2">
                      {[form.city, form.state].filter(Boolean).join(", ")}{form.zip_code ? ` ${form.zip_code}` : ""}
                    </div>
                  )}
                </div>
              </div>
            )}
            <Editable label="ABS Start" type="date" value={form.start_date} editing={editing}
                      className="order-10 sm:order-none" onChange={(v) => set("start_date", v)} testId="ops-field-abs-start"
                      hint="ABS start date — the same date Field Ops shows on the job." />
            <Editable label="ABS Completion" type="date" value={form.abs_completion_date} editing={editing}
                      className="order-10 sm:order-none" onChange={(v) => set("abs_completion_date", v)} testId="ops-field-abs-completion"
                      hint="ABS target completion — the same date Field Ops shows on the job." />
          </div>

          {project.identity.map_embed_url ? (
            <div className="mt-4 overflow-hidden rounded-lg border border-slate-200"
                 data-testid="ops-info-map">
              <iframe title="Job location" src={project.identity.map_embed_url}
                      className="h-44 w-full border-0" loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade" />
              <a href={project.identity.map_url} target="_blank" rel="noreferrer"
                 className="flex items-center gap-1 border-t border-slate-100 px-3 py-1.5 text-xs font-medium text-blue-600 hover:underline"
                 data-testid="ops-map-link">
                Open in maps <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          ) : (
            <NeedNote className="mt-4" testId="ops-info-map-empty">
              Add an address and the map appears here.
            </NeedNote>
          )}

          <div className="mt-5 border-t border-slate-100 pt-4">
            <NoteLog notes={project.notes || []} onAdd={addNote} testId="ops-note-log" />
          </div>

          <div className="mt-5 border-t border-slate-100 pt-4 lg:mt-auto" data-testid="ops-info-general-bottom">
            <div className="lg:mt-5">
              <Requirements group="general" requirements={req} onSave={saveRequirements}
                            testId="ops-requirements-general" />
            </div>
          </div>
        </Card>

        {/* ── Directory ──────────────────────────────────────────── */}
        <div className="flex flex-col [&>section]:flex-1">
        <Card title="Directory" testId="ops-info-directory"
              right={<Button variant="outline" size="sm" onClick={() => setRouting(true)}
                             data-testid="ops-set-emails">
                <Send className="mr-1.5 h-3.5 w-3.5" /> Set emails
              </Button>}>
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              ABS Team
            </p>
            <button onClick={() => setAddRole(addRole ? null : "open")}
                    data-testid="ops-abs-add" title="Put somebody on this job"
                    className="rounded-md border border-slate-200 px-1.5 hover:border-slate-400">
              {addRole ? <X className="h-3.5 w-3.5 text-slate-500" />
                : <Plus className="h-3.5 w-3.5 text-slate-500" />}
            </button>
          </div>

          {addRole && (
            <div className="mt-2 space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3"
                 data-testid="ops-abs-add-form">
              <select value={pickRole} onChange={(e) => setPickRole(e.target.value)}
                      data-testid="ops-abs-pick-role"
                      className="w-full rounded-md border border-slate-200 px-2 py-1 text-xs">
                {teamRoles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
              {newPersonMode ? (
                <>
                  {[["name", "Name"], ["email", "Email"], ["phone", "Phone"]].map(([k, label]) => (
                    <Input key={k} value={newPerson[k]} placeholder={label}
                           onChange={(e) => setNewPerson((c) => ({ ...c, [k]: e.target.value }))}
                           className="h-8 text-xs" data-testid={`ops-abs-new-${k}`} />
                  ))}
                  <select value={newPerson.app_role}
                          onChange={(e) => setNewPerson((c) => ({ ...c, app_role: e.target.value }))}
                          data-testid="ops-abs-new-app-role"
                          className="w-full rounded-md border border-slate-200 px-2 py-1 text-xs">
                    {[["pm", "Project Manager"], ["foreman", "Foreman"],
                      ["estimator", "Estimator"], ["account_manager", "Account Manager"],
                      ["admin", "Admin"]].map(([v, l]) => (
                      <option key={v} value={v}>{l}</option>
                    ))}
                  </select>
                  <div className="flex items-center justify-between gap-2">
                    <button onClick={() => setNewPersonMode(false)}
                            data-testid="ops-abs-pick-existing"
                            className="text-[11px] font-medium text-blue-600 hover:underline">
                      Pick from our people instead
                    </button>
                    <Button size="sm" onClick={() => createPerson(pickRole)}
                            data-testid="ops-abs-new-save">
                      Add to this job
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <select value="" data-testid="ops-abs-pick-person"
                          onChange={(e) => { assign(pickRole, e.target.value); setAddRole(null); }}
                          className="w-full rounded-md border border-slate-200 px-2 py-1 text-xs">
                    <option value="">Pick somebody…</option>
                    {users.filter((u) => !(teamRoles.find((r) => r.key === pickRole)?.emails || [])
                      .includes(u.email))
                      .map((x) => <option key={x.email} value={x.email}>{x.name || x.email}</option>)}
                  </select>
                  <button onClick={() => setNewPersonMode(true)} data-testid="ops-abs-new-toggle"
                          className="text-[11px] font-medium text-blue-600 hover:underline">
                    Not in the list? Add somebody new
                  </button>
                </>
              )}
            </div>
          )}

          <div className="mt-2 space-y-2" data-testid="ops-abs-team">
            {teamRoles.filter((r) => r.emails.length > 0).length === 0 && (
              <NeedNote testId="ops-abs-empty">
                Nobody is on this job yet — use the + to build the team.
              </NeedNote>
            )}
            {teamRoles.filter((r) => r.emails.length > 0).map((role) => (
              <div key={role.key} className="flex gap-2" data-testid={`ops-abs-role-${role.key}`}>
                <span className="w-28 shrink-0 pt-0.5 text-xs font-medium text-slate-500">
                  {role.label}
                </span>
                <ul className="min-w-0 flex-1 space-y-0.5">
                  {role.emails.map((email) => (
                    <li key={email} className="group flex items-baseline gap-2 text-xs"
                        data-testid={`ops-abs-person-${role.key}-${email}`}>
                      <span className="truncate font-medium text-slate-800">
                        {members[email]?.name || email}
                      </span>
                      <a href={`mailto:${email}`} title={email}
                         className="text-slate-400 hover:text-blue-600"
                         data-testid={`ops-abs-email-${email}`}>
                        <Mail className="h-3 w-3" />
                      </a>
                      {members[email]?.phone && (
                        <a href={`tel:${members[email].phone}`} title={members[email].phone}
                           className="text-slate-400 hover:text-blue-600">
                          <Phone className="h-3 w-3" />
                        </a>
                      )}
                      <button onClick={() => unassign(role.key, email)}
                              data-testid={`ops-abs-remove-${role.key}-${email}`}
                              className="ml-auto opacity-0 transition-opacity group-hover:opacity-100">
                        <Trash2 className="h-3.5 w-3.5 text-slate-400 hover:text-rose-600" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-5 border-t border-slate-100 pt-4">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">GC Team</p>
              <button onClick={() => setAdding(adding ? null : "gc")} data-testid="ops-gc-add"
                      disabled={!options?.company_id}
                      title={options?.company_id ? "Add a contact to this customer"
                        : "Link a customer company on this job first — a contact belongs to a company"}
                      className="rounded-md border border-slate-200 px-1.5 hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-40">
                <Plus className="h-3.5 w-3.5 text-slate-500" />
              </button>
            </div>
            {options && !options.company_id && (
              <NeedNote className="mt-1" testId="ops-gc-no-company">
                No customer company is linked to this job yet, so there is no directory to add
                a contact to.
              </NeedNote>
            )}
            <ul className="mt-2 space-y-1 text-xs text-slate-600" data-testid="ops-gc-contacts">
              {contacts.length === 0 && (
                <li><NeedNote testId="ops-gc-no-contacts">
                  No contacts on this customer yet — add one with the + button.
                </NeedNote></li>
              )}
              {contacts.map((c) => (
                <li key={c.id} className="flex flex-wrap items-center gap-2">
                  <Checkbox checked={gcPicked.includes(c.id)}
                            onCheckedChange={() => toggleGcContact(c.id)}
                            data-testid={`ops-gc-pick-${c.id}`} />
                  <span className="font-medium text-slate-800">{c.name}</span>
                  {c.role && <span className="text-slate-400">{c.role}</span>}
                  {c.email && (
                    <a href={`mailto:${c.email}`} data-testid={`ops-gc-email-${c.id}`}
                       className="inline-flex items-center gap-1 text-blue-600 hover:underline">
                      <Mail className="h-3 w-3" />{c.email}
                    </a>
                  )}
                  {(c.phone || c.mobile) && (
                    <a href={`tel:${c.phone || c.mobile}`} data-testid={`ops-gc-phone-${c.id}`}
                       className="inline-flex items-center gap-1 text-blue-600 hover:underline">
                      <Phone className="h-3 w-3" />{c.phone || c.mobile}
                    </a>
                  )}
                </li>
              ))}
            </ul>
            {adding && (
              <div className="mt-3 space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3"
                   data-testid="ops-gc-add-form">
                {[["name", "Name"], ["role", "Role"], ["email", "Email"], ["phone", "Phone"]]
                  .map(([k, label]) => (
                    <Input key={k} value={newContact[k]} placeholder={label}
                           onChange={(e) => setNewContact((c) => ({ ...c, [k]: e.target.value }))}
                           className="h-8 text-xs" data-testid={`ops-gc-new-${k}`} />
                  ))}
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setAdding(null)}
                          data-testid="ops-gc-new-cancel">Cancel</Button>
                  <Button size="sm" onClick={createContact}
                          data-testid="ops-gc-new-save">Add to directory</Button>
                </div>
              </div>
            )}
          </div>
        </Card>

        </div>

        {/* ── Financial summary ───────────────────────────────────── */}
        <Card title="Financial Summary" testId="ops-info-financials" className="flex flex-col select-none [&_input]:select-text [&_textarea]:select-text">
          <dl className="space-y-2 text-sm">
            {[["Base Contract", money(f.original_contract)],
              ["Change Orders", money(f.change_orders_total),
               "Includes processed approved and process unapproved change orders."],
              ["Current Contract", money(f.current_contract)],
              ["Base GM %", f.base_margin_pct == null ? "—" : `${(f.base_margin_pct * 100).toFixed(1)}%`],
              ["Current GM %", f.current_margin_pct == null ? "—" : `${(f.current_margin_pct * 100).toFixed(1)}%`]].map(([k, v, tip]) => (
              <div key={k} className="flex justify-between gap-4" data-testid={`ops-fin-row-${k.replace(/[^a-z]/gi, "").toLowerCase()}`}>
                <dt className="text-slate-500" data-testid={k === "Change Orders" ? "ops-info-change-orders-label" : undefined}><Hint text={tip}>{k}</Hint></dt>
                <dd className={`font-medium ${v === "—" ? "text-slate-400" : "text-slate-900"}`}>{v}</dd>
              </div>
            ))}
          </dl>
          {f.billing?.has_data && (
            <dl className="mt-3 space-y-2 border-t border-slate-100 pt-3 text-sm"
                data-testid="ops-info-billing">
              {[["Billed To Date", f.billing.billed_to_date],
                ["Retainage Held", f.billing.retainage_held],
                ["Awaiting Payment", f.billing.outstanding],
                ["Paid To Date", f.billing.paid_to_date]].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4">
                  <dt className="text-slate-500">{k}</dt>
                  <dd className="font-medium text-slate-900">{money(v)}</dd>
                </div>
              ))}
              {f.billing.last_application && (
                <p className="text-xs text-slate-400">Through {f.billing.last_application}</p>
              )}
            </dl>
          )}

          <div className="mt-5 border-t border-slate-100 pt-4" data-testid="ops-info-contract">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Contract Record
            </p>
            <div className="mt-2 grid gap-3 sm:grid-cols-2">
              {CONTRACT_FIELDS.map(([key, label, type]) => (
                <label key={key} className="block">
                  <span className="text-xs font-medium text-slate-500">{label}</span>
                  <Input type={type} value={form[key] ?? ""}
                         onChange={(e) => set(key, type === "number"
                           ? (e.target.value === "" ? null : Number(e.target.value))
                           : e.target.value)}
                         className="mt-1 h-9 text-sm"
                         data-testid={`ops-field-${key.replace("ops_", "").replace(/_/g, "-")}`} />
                </label>
              ))}
            </div>
            <dl className="mt-3 space-y-2 text-sm">
              <div className="flex justify-between gap-4">
                <dt className="text-slate-500">Tax</dt>
                <dd className="font-medium text-slate-900" data-testid="ops-info-tax-rate">
                  {pct(req.tax_rate)}
                </dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-slate-500">Textura</dt>
                <dd className="font-medium text-slate-900" data-testid="ops-info-textura">
                  {req.textura_enabled ? pct(req.textura_rate) : "not used"}
                </dd>
              </div>
            </dl>
            {!project.contract.has_executed_contract
              && (project.checklist?.missing || []).includes("Executed contract") && (
              <NeedNote className="mt-3" testId="ops-contract-doc-state">
                Executed contract not uploaded
              </NeedNote>
            )}
            <AccelerationPanel projectId={project.id} mode="accept" />
          </div>

          <div className="mt-5 border-t border-slate-100 pt-4" data-testid="ops-info-financial-requirements">
            <Requirements group="financial" requirements={req} onSave={saveRequirements}
                          testId="ops-requirements-financial" />
          </div>

        </Card>
      </div>

      {/* ── Scope and Terms ───────────────────────────────────────── */}
      <Card title="Scope and Terms" testId="ops-info-scope">
        <div className="overflow-hidden rounded-lg border border-slate-200">
          <table className="w-full text-sm" data-testid="ops-major-scopes">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Cost Code</th>
                <th className="px-3 py-2 text-left">Scope</th>
                <th className="px-3 py-2 text-right">Hours</th>
                <th className="px-3 py-2 text-right">Revenue</th>
                <th className="px-3 py-2 text-right">GM %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {(project.scope.major_scopes || []).length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">
                  No scopes on the working estimate yet.
                </td></tr>
              )}
              {(project.scope.major_scopes || []).map((s) => (
                <tr key={s.section} data-testid={`ops-scope-row-${s.section}`}>
                  <td className="px-3 py-1.5 font-mono text-xs">{s.cost_code || "—"}</td>
                  <td className="px-3 py-1.5">{s.section}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {s.hours ? s.hours.toLocaleString() : "—"}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {s.has_budget ? money(s.revenue) : "—"}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">
                    {s.has_budget ? pct(s.gm_pct) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4">
          <Collapsible title="Contract Inclusions, Exclusions, Qualifications and GC Acknowledgements"
                       count={termsCount} testId="ops-contract-terms">
            <div className="grid gap-5 md:grid-cols-3">
              {TERMS.map(([key, label]) => listBlock(key, label))}
            </div>
          </Collapsible>
        </div>

        <div className="mt-5 border-t border-slate-100 pt-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Handoff Notes
          </p>
          <Textarea rows={3} value={form.ops_reconciliation_notes ?? ""}
                    onChange={(e) => set("ops_reconciliation_notes", e.target.value)}
                    placeholder="The catch-all: anything from the handoff call that has no home above"
                    className="mt-2 text-sm" data-testid="ops-field-reconciliation-notes" />
        </div>
      </Card>

      {routing && (
        <EmailRoutingDialog project={project} people={routingPeople}
                            onClose={() => setRouting(false)} onSaved={reload} />
      )}
    </div>
  );
}
